import { Check } from '@mui/icons-material'
import {
  Backdrop,
  Box,
  Button,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'

import libbyTalkingRightSideSrc from '~/assets/images/libby/libby-talking-right-side.webp'
import getBenefits from '~/components/upgradeToUnlock/utils'
import useUserAccess from '~/features/auth/hooks/useUserAccess'
import useIsPartner from '~/features/partners/hooks/useIsPartner'
import usePartnerCentralized from '~/features/partners/hooks/usePartnerCentralized'
import { EClientPath } from '~/models/api'
import rem from '~/utils/ui'

type TProps = {
  isOpen: boolean
  onClose: () => void
}

function UpgradeToUnlock({ isOpen, onClose }: TProps) {
  const theme = useTheme()
  const navigate = useNavigate()
  const isPartner = useIsPartner()

  const { partner } = usePartnerCentralized()

  const { isBasicFreeTrial } = useUserAccess()

  return (
    <Backdrop
      sx={{
        zIndex: theme.zIndex.tooltip + 1,
      }}
      open={isOpen}
      onClick={() => onClose()}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xxxs: 'column', sm: 'row' },
          width: { xxxs: '90%', sm: 600 },
          minHeight: `min(${rem(300)}, '90%')`,
          maxHeight: '90%',
          minWidth: 250,
          backgroundColor: theme.palette.common.white,
          overflow: 'auto',
        }}
      >
        <Stack
          sx={{
            backgroundColor: theme.palette.common.white,
            gap: 4,
            paddingBlock: 4,
            paddingInline: isBasicFreeTrial ? 2 : 4,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.mainFont,
              textAlign: 'center',
              fontSize: isBasicFreeTrial ? rem(16) : rem(20),
            }}
          >
            {isBasicFreeTrial
              ? 'Upgrade to keep this feature after the free trial'
              : 'Upgrade to unlock this feature'}
          </Typography>
          {!isPartner && (
            <Box
              sx={{
                height: 80,
                backgroundImage: `url(${libbyTalkingRightSideSrc})`,
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                flexShrink: 0,
              }}
            />
          )}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 2,
              flexDirection: isPartner ? 'column-reverse' : 'row',
            }}
          >
            <Button variant="secondary" onClick={() => onClose()}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() =>
                navigate(isPartner ? EClientPath.account : EClientPath.billing)
              }
            >
              {isPartner ? 'Learn More' : 'View Plans'}
            </Button>
          </Box>
        </Stack>
        <Stack
          sx={{
            backgroundColor: theme.palette.mainBackground,
            color: theme.palette.mainFont,
            gap: 2,
            p: 2,
            paddingTop: 4,
            alignItems: 'center',
          }}
        >
          <Typography variant="h6">Included in upgrades</Typography>
          <Stack gap={1}>
            {getBenefits(partner.partnerName).map((benefit) => (
              <Box
                key={benefit}
                sx={{
                  display: 'flex',
                  gap: 1,
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                }}
              >
                <Check sx={{ fontSize: 16, color: theme.palette.secondary1 }} />
                <Typography variant="caption" sx={{ fontSize: '1rem' }}>
                  {benefit}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Stack>
      </Box>
    </Backdrop>
  )
}

export default UpgradeToUnlock
